import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { MapPin, ArrowRight, TreePine, BarChart3, FileText, BadgeCheck, Users, Leaf, Shield, Globe } from 'lucide-react'
import SEO from '../components/seo/SEO'
import Button from '../components/ui/Button'
import ImpactMetric from '../components/ui/ImpactMetric'
import { useLocalePath } from '../hooks/useLocalePath'
import styles from './LizaCarbon.module.css'

const MRV_STEPS = [
  { title: 'Baseline Assessment', description: 'Comprehensive mapping of forest cover, carbon stocks, and deforestation drivers across the 42,600-hectare project area using satellite imagery and ground-truth surveys.', icon: TreePine },
  { title: 'Monitoring', description: 'Continuous remote sensing combined with community-based forest patrols and permanent sample plots to track deforestation rates and carbon stock changes.', icon: BarChart3 },
  { title: 'Reporting', description: 'Annual monitoring reports documenting emission reductions, community benefits, biodiversity indicators, and safeguard compliance against the VM0007 methodology.', icon: FileText },
  { title: 'Verification', description: 'Independent third-party verification by an accredited VVB, leading to verified carbon credit issuance on the Verra VCS registry.', icon: BadgeCheck },
]

const PROJECT_SERVICES = [
  { title: 'Project Design Documents', description: 'Full PDD development under VM0007 methodology for Verra VCS registration.', icon: FileText },
  { title: 'MRV Services', description: 'End-to-end measurement, reporting and verification cycle including remote sensing and field protocols.', icon: BarChart3 },
  { title: 'Carbon Credit Verification', description: 'VVB coordination, audit preparation, and credit issuance support on the Verra registry.', icon: BadgeCheck },
  { title: 'Piloting Programs', description: 'Initial pilot phase in three community zones before full-scale implementation.', icon: Shield },
]

export default function LizaCarbon() {
  const { t } = useTranslation('projects')
  const lp = useLocalePath()

  return (
    <div className={styles.page}>
      <SEO
        title="LIZA Carbon Project | Carbon13"
        description="A large-scale ARR and REDD+ project in the Congo Basin protecting 42,600 hectares of tropical rainforest while providing sustainable livelihoods for indigenous communities."
        canonical="/projects/liza-carbon"
      />

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />
        <div className={styles.container}>
          <motion.div
            className={styles.heroContent}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className={styles.eyebrow}>REDD+ Carbon Project</span>
            <h1 className={styles.heroTitle}>LIZA Carbon Project</h1>
            <div className={styles.heroLocation}>
              <MapPin size={18} />
              <span>Republic of the Congo</span>
            </div>
            <p className={styles.heroLead}>
              Protecting critical Congo Basin rainforest through avoided deforestation, sustainable land management, and community-based conservation.
            </p>
            <div className={styles.heroBadges}>
              <span className={styles.heroBadge}>VM0007 Methodology</span>
              <span className={styles.heroBadge}>Verra VCS Registry</span>
              <span className={styles.heroBadge}>Active Since 2023</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className={styles.impact}>
        <div className={styles.container}>
          <motion.div
            className={styles.impactGrid}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <ImpactMetric value={207510} label={t('liza.metricCO2')} suffix=" tCO₂e/yr" />
            <ImpactMetric value={42600} label={t('liza.metricHectares')} suffix=" ha" />
            <ImpactMetric value={2400} label={t('liza.metricCommunities')} />
            <ImpactMetric value={4} label={t('liza.metricSpecies')} />
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className={styles.overview}>
        <div className={styles.container}>
          <div className={styles.overviewGrid}>
            <motion.div
              className={styles.overviewText}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="eyebrow">Project Overview</span>
              <h2>Preserving the Congo Basin</h2>
              <p>
                The LIZA Carbon Project is a large-scale ARR and REDD+ initiative operating across 42,600 hectares of tropical rainforest in the Republic of the Congo. The Congo Basin is the world's second-largest tropical forest, a critical carbon sink absorbing approximately 1.2 billion tonnes of CO2 annually.
              </p>
              <p>
                Our project addresses the primary drivers of deforestation in the region: slash-and-burn agriculture, illegal logging, and charcoal production. Through a combination of community engagement, alternative livelihood programmes, and forest monitoring, we work to reduce deforestation rates while improving the quality of life for over 2,400 indigenous people.
              </p>
              <p>
                The project is registered under the Verra Verified Carbon Standard (VCS) using the VM0007 REDD+ Methodology Framework, ensuring the highest standards of carbon accounting and environmental integrity.
              </p>
            </motion.div>
            <motion.div
              className={styles.overviewDetails}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className={styles.detailCard}>
                <h4>Project Details</h4>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Location</span>
                  <span className={styles.detailValue}>Republic of the Congo</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Methodology</span>
                  <span className={styles.detailValue}>VM0007 - REDD+ Methodology Framework</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Registry</span>
                  <span className={styles.detailValue}>Verra VCS</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Start Year</span>
                  <span className={styles.detailValue}>2023</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status</span>
                  <span className={styles.statusActive}>Active</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Project Area</span>
                  <span className={styles.detailValue}>42,600 hectares</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Crediting Period</span>
                  <span className={styles.detailValue}>30 years</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MRV Pipeline */}
      <section className={styles.mrv}>
        <div className={styles.container}>
          <motion.div
            className={styles.mrvHeader}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">Measurement, Reporting & Verification</span>
            <h2>MRV Pipeline</h2>
            <p className={styles.mrvSubtitle}>
              Our rigorous four-stage MRV process ensures every tonne of CO2 is accurately measured, transparently reported, and independently verified.
            </p>
          </motion.div>

          <div className={styles.mrvGrid}>
            {MRV_STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                className={styles.mrvStep}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className={styles.mrvStepHeader}>
                  <span className={styles.mrvStepNumber}>{String(i + 1).padStart(2, '0')}</span>
                  <div className={styles.mrvStepIcon}>
                    <step.icon size={20} />
                  </div>
                </div>
                {i < MRV_STEPS.length - 1 && <div className={styles.mrvConnector} />}
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className={styles.services}>
        <div className={styles.container}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">Our Contribution</span>
            <h2>Services We Provide</h2>
          </motion.div>

          <div className={styles.servicesGrid}>
            {PROJECT_SERVICES.map((svc, i) => (
              <motion.div
                key={svc.title}
                className={styles.serviceItem}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className={styles.serviceIcon}>
                  <svc.icon size={22} />
                </div>
                <h3>{svc.title}</h3>
                <p>{svc.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className={styles.approach}>
        <div className={styles.container}>
          <div className={styles.approachGrid}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="eyebrow">Our Approach</span>
              <h2>Community-Centred Conservation</h2>
              <p>
                The LIZA Carbon Project is built on the principle that effective conservation must directly benefit the communities who depend on the forest. Our approach integrates three pillars:
              </p>
            </motion.div>
            <div className={styles.pillars}>
              <motion.div className={styles.pillar} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                <div className={styles.pillarIcon}><Leaf size={24} /></div>
                <h4>Sustainable Livelihoods</h4>
                <p>Agroforestry training, improved cookstove distribution, and alternative income programmes that reduce pressure on forest resources while increasing household income.</p>
              </motion.div>
              <motion.div className={styles.pillar} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                <div className={styles.pillarIcon}><Users size={24} /></div>
                <h4>Community Governance</h4>
                <p>Establishing community forest management committees, transparent benefit-sharing mechanisms, and participatory monitoring systems that empower local stewardship.</p>
              </motion.div>
              <motion.div className={styles.pillar} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
                <div className={styles.pillarIcon}><Globe size={24} /></div>
                <h4>Biodiversity Protection</h4>
                <p>Protecting habitat for 4 protected species including endangered primates and endemic bird species through targeted conservation corridors and anti-poaching patrols.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Stakeholders */}
      <section className={styles.stakeholders}>
        <div className={styles.container}>
          <motion.div
            className={styles.stakeholderContent}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">Partnerships</span>
            <h2>Working Together for Impact</h2>
            <p>
              The LIZA Carbon Project brings together a diverse coalition of stakeholders committed to forest conservation and community development. We work closely with local communities, provincial government agencies, international verification bodies, and technical partners to ensure the project delivers lasting environmental and social impact.
            </p>
            <div className={styles.stakeholderGrid}>
              <div className={styles.stakeholderItem}>
                <Users size={20} />
                <span>Local Communities</span>
              </div>
              <div className={styles.stakeholderItem}>
                <Shield size={20} />
                <span>Government Agencies</span>
              </div>
              <div className={styles.stakeholderItem}>
                <BadgeCheck size={20} />
                <span>International Verifiers</span>
              </div>
              <div className={styles.stakeholderItem}>
                <Globe size={20} />
                <span>NGO Partners</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.cta}>
        <div className={styles.container}>
          <motion.div
            className={styles.ctaContent}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Interested in Carbon Projects?</h2>
            <p>
              Whether you are looking to develop a new REDD+ project, offset your corporate emissions, or invest in high-quality carbon credits, we would like to hear from you.
            </p>
            <Button variant="secondary" size="lg" href={lp('/contact')}>
              Get in Touch <ArrowRight size={18} />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
